/**
 * Releases Service - API calls for version history management
 */

const API_BASE = import.meta.env.VITE_API_URL || 'https://apicabaza.creago.be';

/**
 * Get all releases
 * @param {Object} options - Optional filters (type, limit, offset)
 * @returns {Promise<Array>} List of releases
 */
export const getReleases = async (options = {}) => {
    try {
        let url = `${API_BASE}/rest/cabaza/releases`;
        const params = [];

        if (options.type) params.push(`type=${options.type}`);
        if (options.limit) params.push(`limit=${options.limit}`);
        if (options.offset) params.push(`offset=${options.offset}`);

        if (params.length > 0) url += '?' + params.join('&');

        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch releases');

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        return success ? data : [];
    } catch (error) {
        console.error('Error fetching releases:', error);
        return [];
    }
};

/**
 * Get a specific release by version
 * @param {string} version - Version string (e.g., "v0.45")
 * @returns {Promise<Object|null>} Release or null
 */
export const getReleaseByVersion = async (version) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/releases/version/${encodeURIComponent(version)}`);
        if (!response.ok) throw new Error('Failed to fetch release');

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA;

        return success ? data : null;
    } catch (error) {
        console.error('Error fetching release:', error);
        return null;
    }
};

/**
 * Get the latest release
 * @returns {Promise<Object|null>} Latest release or null
 */
export const getLatestRelease = async () => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/releases/latest`);
        if (!response.ok) throw new Error('Failed to fetch latest release');

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA;

        return success ? data : null;
    } catch (error) {
        console.error('Error fetching latest release:', error);
        return null;
    }
};

/**
 * Create a new release
 * @param {Object} data - Release data
 * @returns {Promise<Object>} Result with success status and id
 */
export const createRelease = async (data) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/releases`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });

        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS ?? false,
            id: result.id ?? result.ID ?? 0,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error creating release:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Update a release
 * @param {number} id - Release ID
 * @param {Object} data - Update data
 * @returns {Promise<Object>} Result with success status
 */
export const updateRelease = async (id, data) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/releases/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });

        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error updating release:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Delete a release
 * @param {number} id - Release ID
 * @returns {Promise<Object>} Result with success status
 */
export const deleteRelease = async (id) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/releases/${id}`, {
            method: 'DELETE'
        });

        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error deleting release:', error);
        return { success: false, message: error.message };
    }
};

// ============ RELEASE TYPES ============

export const RELEASE_TYPES = {
    feature: { label: { fr: 'Fonctionnalité', en: 'Feature', nl: 'Functie' }, color: 'green' },
    fix: { label: { fr: 'Correction', en: 'Fix', nl: 'Fix' }, color: 'red' },
    performance: { label: { fr: 'Performance', en: 'Performance', nl: 'Prestatie' }, color: 'yellow' },
    refactor: { label: { fr: 'Refactoring', en: 'Refactor', nl: 'Refactoring' }, color: 'blue' },
    i18n: { label: { fr: 'Traductions', en: 'Translations', nl: 'Vertalingen' }, color: 'purple' },
    milestone: { label: { fr: 'Jalon', en: 'Milestone', nl: 'Mijlpaal' }, color: 'gray' }
};
